// src/components/WizardAgendamento/CalendarGrid.tsx
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { WizardAvailableDay } from '@/services/appointment-wizard-service';

interface Props {
  availability: WizardAvailableDay[];
  onSelectDay: (date: string) => void;
  selectedDate?: string;
  currentMonth: Date;
  onMonthChange: (newMonth: Date) => void;
}

export const CalendarGrid: React.FC<Props> = ({ 
  availability, onSelectDay, selectedDate, currentMonth, onMonthChange 
}) => {
  const daysOfWeek = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
  
  const monthName = currentMonth.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

  // 1. Cálculo de dias para alinhar o grid
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  
  // Criamos os espaços vazios iniciais
  const blanks = Array.from({ length: firstDayOfMonth }, (_, i) => i);
  // Criamos a lista de dias reais (1 a 28/30/31)
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const shiftMonth = (offset: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
    onMonthChange(newDate);
  };

  return (
    <div className="w-full border rounded-lg overflow-hidden bg-white shadow-sm">
      {/* Navegação de Mês */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b">
        <span className="text-xs font-bold text-slate-700 capitalize">{monthName}</span>
        <div className="flex gap-1">
          <button onClick={() => shiftMonth(-1)} className="p-1 hover:bg-slate-200 rounded transition-colors">
            <ChevronLeft className="h-4 w-4 text-slate-600" />
          </button>
          <button onClick={() => shiftMonth(1)} className="p-1 hover:bg-slate-200 rounded transition-colors">
            <ChevronRight className="h-4 w-4 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Cabeçalho dias da semana */}
      <div className="grid grid-cols-7 bg-slate-100/50 border-b">
        {daysOfWeek.map((d, i) => (
          <div key={i} className="text-center py-1 text-[10px] font-black text-slate-400 uppercase">{d}</div>
        ))}
      </div>

      {/* Grid do Calendário com correção de alinhamento */}
      <div className="grid grid-cols-7 gap-px bg-slate-200">
        
        {/* Renderiza os espaços vazios (paddings) */}
        {blanks.map((_, i) => (
          <div key={`blank-${i}`} className="h-10 bg-slate-50/50" />
        ))}

        {/* Renderiza os dias do mês baseados no calendário real */}
        {days.map((dayNumber) => {
          // Formata a data atual do loop para comparar com a disponibilidade
          const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(dayNumber).padStart(2, '0')}`;
          
          // Busca os dados de disponibilidade para este dia específico
          const dayData = availability.find(a => a.slot_date === dateStr);
          const isSelected = selectedDate === dateStr;
          const hasSlots = dayData && dayData.free_slots > 0;

          return (
            <button
              key={dateStr}
              type="button"
              disabled={!hasSlots}
              onClick={() => onSelectDay(dateStr)}
              className={`h-10 p-0.5 flex flex-col items-center justify-center transition-all relative
                ${hasSlots ? 'bg-white hover:bg-teal-50 cursor-pointer' : 'bg-slate-50 text-slate-300 cursor-not-allowed'}
                ${isSelected ? 'ring-2 ring-inset ring-teal-500 bg-teal-50 z-10 font-bold' : ''}`}
            >
              <div className="flex items-center gap-0.5">
                <span className={`text-xs font-semibold ${hasSlots ? 'text-slate-900' : 'text-slate-300'}`}>
                  {dayNumber}
                </span>
                {hasSlots && (
                  <span className="text-[8px] font-bold text-teal-600 leading-none">
                    {dayData.free_slots}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};